import { Button } from '@/components/Button'
import { Divider } from '@/components/Divider'
import { Segmented } from '@/components/Segmented'
import {
  caseBallotState,
  caseMajorityOfCantonsVote,
  mkOutcome,
  mkPopularVoteVisibility,
  pickMultiLocaleString,
  resultOutcome,
  toFederalBallotSummary,
} from '@/domain'
import * as M from '@/materials'
import * as React from 'react'
import { FormattedDate, FormattedMessage } from 'react-intl'
import { css } from 'styled-components/macro'
import * as R from '../../../apps/ballot/standalone/routes'
import { useEnv } from '../../../lib/env'
import {
  useFederalBallotDocument,
  useFederalBallotsCollection,
  useFirestoreSnapshot,
  usePopularVoteDocument,
} from '../../../lib/index'
import { default as Tile } from './Tile'
import * as firestore from 'firebase/firestore'

interface Props {
  popularVoteId: string
  archive?: boolean

  /**
   * If true, the top divider is placed differently.
   */
  first?: boolean
}

function PopularVote(props: Props) {
  const { history } = useEnv()

  const { popularVoteId, first } = props

  const popularVoteDocument = usePopularVoteDocument({ popularVoteId })
  const federalBallotsCollection = useFederalBallotsCollection({ popularVoteId })

  if (popularVoteDocument && federalBallotsCollection && federalBallotsCollection.length > 0) {
    return (
      <div css={[styles.root, first && styles.first] as any}>
        <Divider variant="strong" css={styles.divider as any} />

        <div css={styles.label as any}>
          <FormattedDate value={popularVoteDocument.day.toDate()} year="numeric" month="long" day="numeric" />
        </div>

        <Segmented css={styles.tiles as any}>
          {federalBallotsCollection.map((doc, i) => (
            <Tile_ key={i} popularVoteId={popularVoteId} federalBallotId={doc.id} />
          ))}
        </Segmented>

        <div css={styles.actions as any}>
          <Button
            css={styles.allResultsButton as any}
            color="secondary"
            onClick={() => {
              history.push(`/${popularVoteId}`)
              window.scrollTo({ top: 0 })
            }}
          >
            <FormattedMessage id="components.Archive.all-results" defaultMessage="Alle Resultate" />
          </Button>
        </div>
      </div>
    )
  } else {
    return null
  }
}

const styles = {
  root: css`
    display: grid;
    grid-template-areas: 'divider divider' 'label label' 'tiles tiles' 'actions actions';
    grid-template-columns: 1fr 1fr;
    column-gap: 28px;

    --tiles-top-margin: 0px;

    @media only screen and (min-width: 1072px) {
      grid-template-areas: 'divider .' 'label tiles' '. actions';
      grid-template-columns: 240px 1fr;

      --tiles-top-margin: -10px;
    }
  `,

  first: css`
    @media only screen and (min-width: 1072px) {
      grid-template-areas: 'divider divider' 'label tiles' '. actions';
      --tiles-top-margin: 25px;
    }
  `,

  divider: css`
    grid-area: divider;
  `,

  label: css`
    grid-area: label;

    ${M.fontHeading5_};
    text-transform: uppercase;

    margin: 10px 0 6px;
  `,

  tiles: css`
    grid-area: tiles;

    margin-bottom: 1.5rem;
    margin-top: var(--tiles-top-margin);
  `,

  tileWrapper: css`
    justify-self: stretch;

    display: flex;
    flex-direction: column;
    padding: var(--gutter-half) var(--gutter-half) 0;

    grid-column: span 6;

    @media only screen and (min-width: 711px) {
      grid-column: span 4;
    }
    @media only screen and (min-width: 1072px) {
      grid-column: span 3;
    }
  `,

  tile: css`
    padding-bottom: var(--gutter-half);
    flex: 1;
  `,

  actions: css`
    grid-area: actions;
  `,
  allResultsButton: css`
    min-width: 156px;
  `,
}

export default React.memo(PopularVote)

function Tile_(props: { popularVoteId: string; federalBallotId: string }) {
  const { intl, history, db } = useEnv()

  const { popularVoteId, federalBallotId } = props

  const popularVoteDocument = usePopularVoteDocument({ popularVoteId })
  const federalBallotDocument = useFederalBallotDocument({
    popularVoteId,
    ballotId: federalBallotId,
  })

  const cantonsSnapshot = useFirestoreSnapshot(
    firestore.collection(db, 'popularVotes', popularVoteId, 'federalBallots', federalBallotId, 'cantons'),
  )

  const visibility = popularVoteDocument ? popularVoteDocument.visibility : mkPopularVoteVisibility('inactive')

  const summary = React.useMemo(() => {
    if (federalBallotDocument && cantonsSnapshot) {
      return toFederalBallotSummary({
        date: new Date(Date.parse(popularVoteId)),
        visibility,
        docSnapshot: {
          id: federalBallotId,
          data() {
            return federalBallotDocument
          },
        } as any,
        cantonsSnapshot,
      })
    } else {
      return undefined
    }
  }, [popularVoteId, federalBallotId, federalBallotDocument, cantonsSnapshot, visibility])

  const state = React.useMemo(() => {
    if (summary) {
      return {
        ballotState: summary.state.ballotState,
        outcome: caseMajorityOfCantonsVote({
          notRequired: undefined,
          undecided: undefined,
          reject: mkOutcome('nay'),
          accept: undefined,
        })(summary.state.majorityOfCantons),
      }
    } else {
      return undefined
    }
  }, [summary])

  if (!state || !summary) {
    return null
  }

  const name = pickMultiLocaleString(intl, summary.name, '<federal ballot name>')

  /*
   * The state is always 'finalResult', as the archive only shows completed
   * ballots.
   */
  const { outcome, finalResult } =
    caseBallotState<null | any>({
      pre: null,
      inProgress: null,
      trend: () => null,
      projection: () => null,
      finalResult: ({ finalResult }) => ({
        outcome: state.outcome ?? resultOutcome(finalResult),
        finalResult,
      }),
    })(state.ballotState) || {}

  const href = R.federalBallot(popularVoteId, federalBallotId)

  return (
    <div css={styles.tileWrapper as any}>
      {finalResult && (
        <Tile
          css={styles.tile as any}
          name={name}
          outcome={outcome}
          finalResult={finalResult}
          onClick={() => {
            history.push(href)
          }}
        />
      )}
      <Divider />
    </div>
  )
}
